<template>
  <wui-card
    ref="container"
    v-click-outside="onOutsideClick"
    class="absolute left-0 top-0 z-20 min-w-[220px] outline-none bg-elev-modal-bg select-none"
    state="text"
    shadow="overlay"
  >
    <header class="flex gap-2 items-center justify-between border-b border-b-bor-nav pb-2">
      <div class="flex items-center gap-2 text-fg-base-fg3">
        <wui-icon
          name="notifications"
          class="w-5! h-5!"
        />
        <p class="text-heading-m-16-22">
          {{ $t('common.alarm') }}
        </p>
      </div>
      <wui-btn
        icon
        prepend-icon="closeRegularMdM"
        variant="neut"
        state="alpha"
        :size="32"
        @click="$emit('close')"
      />
    </header>

    <main class="py-4 space-y-4">
      <p class="text-heading-m-14-20 text-fg-base-fg3 text-center">
        {{ alarm?.name }}
      </p>
      <div>
        <p class="text-heading-m-18-24 text-fg-base-fg3 mb-1 text-center">
          {{ timeToDisplay }}
        </p>
        <p class="text-body-m-14-20 text-fg-base-fg3 text-center">
          {{ dateToDisplay }}
        </p>
      </div>

      <div>
        <p class="text-label-r-13-18 text-fg-base-fg22 text-center mb-1">
          {{ $t('common.regularity') }}
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg3 text-center">
          {{ regularityToDisplay }}
        </p>
      </div>

      <div>
        <p class="text-label-r-13-18 text-fg-base-fg22 text-center mb-1">
          {{ $t('common.audioFile') }}
        </p>
        <p class="text-body-r-14-20 text-fg-base-fg3 text-center">
          {{ promptAlarmToDisplay }}
        </p>
      </div>
    </main>

    <footer
      v-if="alarm"
      class="pt-2 border-t border-t-bor-nav flex gap-2 justify-center"
    >
      <wui-btn
        icon
        :title="t('common.editAlarmClock')"
        variant="brand"
        state="tonal"
        prepend-icon="editM"
        @click="editAlarm"
      />
      <wui-btn
        icon
        auto-loading
        :title="t('common.deleteAlarmClock')"
        variant="negative"
        state="tonal"
        prepend-icon="trashM"
        @click="removeAlarm"
      />
    </footer>
  </wui-card>
</template>

<script lang="ts" setup>
import { WuiIcon, WuiBtn, WuiCard, useDialog, ClickOutside as vClickOutside } from '@wui/common-library'
import dayjs from 'dayjs'
import { storeToRefs } from 'pinia'
import { ref, onMounted, onBeforeUnmount, computed, defineAsyncComponent, nextTick, watch } from 'vue'

import { useStoreAlarm, AlarmSchedule, parseServerAlarmUtc, resolveAlarmUserTimezone } from '@/entities/alarm'

import { useLocalization } from '@/shared/composables/localization'
import { useStoreConfiguration } from '@/shared/store/configuration'

const AlarmItem = defineAsyncComponent(() => import('@/pages/view-settings/notifications/alarm-schedule/alarm-item.vue'))

const props = defineProps<{
  alarmId: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'ref', ref: HTMLDivElement): void
}>()

const { showDialog } = useDialog()

const container = ref<InstanceType<typeof WuiCard> | HTMLDivElement>()
const isClickOutsideEnabled = ref(false)

const enableClickOutside = async () => {
  isClickOutsideEnabled.value = false
  await nextTick()
  window.setTimeout(() => {
    isClickOutsideEnabled.value = true
  }, 0)
}

onMounted(async () => {
  const el = (container.value as { $el?: HTMLDivElement })?.$el ?? container.value
  if (el instanceof HTMLElement) {
    emit('ref', el as HTMLDivElement)
  }

  await enableClickOutside()
})

onBeforeUnmount(() => {
  isClickOutsideEnabled.value = false
})

watch(() => props.alarmId, () => {
  enableClickOutside()
})

const onOutsideClick = () => {
  if (!isClickOutsideEnabled.value) {
    return
  }
  emit('close')
}

const { t } = useLocalization()
const storeAlarm = useStoreAlarm()
const { alarmSchedules, promptAlarms, selectedTimezone } = storeToRefs(storeAlarm)
const { configuration } = storeToRefs(useStoreConfiguration())

const alarm = computed(() => alarmSchedules.value.get(props.alarmId))

const dateTime = computed(() => {
  if (!alarm.value) {
    return null
  }

  const userTz = resolveAlarmUserTimezone(selectedTimezone.value)
  const anchorDate = alarm.value.date
    ? alarm.value.date
    : dayjs().tz(userTz).format('YYYY-MM-DD')

  return parseServerAlarmUtc(
    anchorDate,
    alarm.value.time,
    configuration.value.tzOffset,
  ).tz(userTz)
})

const regularityToDisplay = computed(() => {
  if (!alarm.value) {
    return ''
  }

  if (alarm.value?.date) {
    return t('common.oneTime')
  }

  const weekdaysArr: [boolean | undefined, number, string][] = [
    [alarm.value.sunday,    0, t('common.su')],
    [alarm.value.monday,    1, t('common.mo')],
    [alarm.value.tuesday,   2, t('common.tu')],
    [alarm.value.wednesday, 3, t('common.we')],
    [alarm.value.thursday,  4, t('common.th')],
    [alarm.value.friday,    5, t('common.fr')],
    [alarm.value.saturday,  6, t('common.sa')],
  ]

  return weekdaysArr.filter(item => item[0]).map(item => item[2]).join(', ')
})

const promptAlarmToDisplay = computed(() => {
  if (!alarm.value) {
    return null
  }
  for (const prompt of promptAlarms.value.values()) {
    if (alarm.value.promptGuids.includes(prompt.guid) || alarm.value.promptGuids.includes(prompt.id)) {
      return prompt.name
    }
  }
  return null
})

const dateToDisplay = computed(() => dateTime.value?.format(t('common.dateFormat')))
const timeToDisplay = computed(() => dateTime.value?.format('HH:mm'))

const removeAlarm = async () => {
  await storeAlarm.removeAlarmSchedule({ guid: props.alarmId } as AlarmSchedule)
  emit('close')
}

const editAlarm = () => {
  if (!alarm.value) {
    return
  }
  showDialog(AlarmItem, {
    alarm: alarm.value,
    date: dateTime.value?.format('YYYY-MM-DD') ?? '',
    isModal: true,
  })
}

</script>
